import { Copy, Loader2, Plus } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";

interface DuplicateRecipeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isPending?: boolean;
}

export function DuplicateRecipeDialog({
  open,
  onOpenChange,
  onConfirm,
  isPending = false,
}: DuplicateRecipeDialogProps) {
  const { t } = useLingui();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        className="max-w-[500px] p-0 gap-0 overflow-hidden rounded-xl border border-(--shell-border)"
      >
        {/* Illustration block */}
        <div className="relative flex h-[248px] items-center justify-center bg-(--color-surface-muted) border-b border-(--color-border) overflow-hidden">
          <div className="relative">
            <Copy
              className="absolute -top-1.5 left-1.5 size-40 text-(--gray-300)"
              strokeWidth={1}
            />
            <Copy
              className="relative size-40 text-(--color-text-primary)"
              strokeWidth={1}
            />
          </div>
          <Plus
            className="absolute top-12 right-[148px] size-10 text-(--gray-300)"
            strokeWidth={1.5}
          />
          <Plus
            className="absolute bottom-14 left-[148px] size-10 text-(--gray-300)"
            strokeWidth={1.5}
          />
          <Plus
            className="absolute top-3 left-[196px] size-7 text-(--gray-300)"
            strokeWidth={1.5}
          />
          <span className="absolute top-[78px] left-[212px] size-1.5 rounded-full bg-(--color-surface-accent)" />
          <span className="absolute bottom-[44px] right-[140px] size-1.5 rounded-full bg-(--color-surface-accent)" />
          <span className="absolute top-[40px] left-[197px] size-1.5 rounded-full bg-(--color-surface-accent)" />
        </div>

        {/* Text block */}
        <div className="flex flex-col items-center gap-3 p-5 text-center">
          <DialogTitle className="text-2xl font-medium text-(--color-text-primary)">
            {t`Duplicate Recipe?`}
          </DialogTitle>
          <p className="text-base text-(--color-text-secondary)">
            {t`A copy of this recipe will be created. You'll be able to edit and update the duplicated version before publishing.`}
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3 p-5">
          <Button
            variant="outline"
            className="flex-1 h-[52px] rounded-[10px] border-(--shell-border) text-base font-medium text-(--color-text-primary)"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {t`Cancel`}
          </Button>
          <Button
            className="flex-1 h-[52px] rounded-[10px] bg-(--color-action) hover:bg-(--color-action)/90 text-(--color-action-fg) text-base font-medium"
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
            {t`Duplicate recipe`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
